import React, { FC } from 'react';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';

import { InfoCircle } from '../../../../assets/icons/index';
import {
  AnimatedCalculatedInput,
  AnimatedNumber,
} from '../../../../components/animated-number/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { FormDatePicker } from '../../../../components/date-picker/index';
import { DropDown } from '../../../../components/dropdown/index';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '../../../../components/hover-card/index';
import { FormInput } from '../../../../components/input/index';
import { useFormat, useLocale } from '../../../../hooks/index';
import { toMathRound } from '../../../../utils/to-math-round.util';
import { EventCombobox } from '../components/index';
import { defaultSharePrice, FormSchema } from '../event-form.validation';

export type ShareIssuanceStepOneProps = {
  companyId: string;
  control: Control<FormSchema>;
  issuedShares: number;
  lockMode: boolean;
  setFormData: UseFormSetValue<FormSchema>;
};

export const ShareIssuanceStepOne: FC<ShareIssuanceStepOneProps> = ({
  companyId,
  control,
  lockMode,
  setFormData,
  issuedShares,
}) => {
  const { messagesLocale } = useLocale();
  const { stepOne } = useWatch<FormSchema>({ control });
  const { format } = useFormat();

  const event = stepOne?.eventDetails?.['share-issuance']?.event;
  const preMoneyValuation =
    event?.sharePrice &&
    event?.sharePrice !== defaultSharePrice &&
    event?.issuedSharesOnStart !== undefined
      ? event?.sharePrice * event?.issuedSharesOnStart
      : event?.sharePrice && event?.sharePrice !== defaultSharePrice
        ? event?.sharePrice * issuedShares
        : 0;

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex w-full flex-col gap-4">
        <EventCombobox
          companyId={companyId}
          control={control}
          fieldName={`stepOne.eventDetails.share-issuance.event`}
          lockMode={lockMode}
        />
        <FormDatePicker
          iconColor="#98A2B3"
          inputValue={
            stepOne?.eventDetails?.['share-issuance']?.date
              ? format(stepOne?.eventDetails?.['share-issuance']?.date, 'dd/MM/yyyy')
              : ''
          }
          inputWrapperClassName="w-full"
          onSelect={(date) => {
            setFormData('stepOne.eventDetails.share-issuance.date', date);
          }}
          placeholder={<AppFormattedMessage id="DATE" />}
          value={stepOne?.eventDetails?.['share-issuance']?.date}
        />
        <FormInput
          control={control}
          currencySign
          disabled={stepOne?.eventDetails?.['share-issuance']?.event?.id !== 'new'}
          maxDecimalPlaces={4}
          name={'stepOne.eventDetails.share-issuance.event.sharePrice'}
          placeholder={<AppFormattedMessage id="SHARES_PRICE" />}
          shouldDisplayZero
          shouldFormatNumber
          transform={(value) => {
            return value !== undefined && value !== null && Number(value) !== defaultSharePrice
              ? toMathRound(value, 4).toString()
              : '';
          }}
          wrapperClassName="w-full"
        />
      </div>
      <div className="flex flex-col gap-3 rounded-md bg-brand-25 px-1 pb-1 pt-3">
        <div className="flex flex-col items-start gap-1 pl-[14px]">
          <div className="flex items-center gap-2">
            <span className="text-sm font-[550] text-brand-700">
              <AppFormattedMessage id="PRE_MONEY_VALUATION" />
            </span>
            <HoverCard>
              <HoverCardTrigger>
                <InfoCircle className="size-4 cursor-pointer" iconColor="#2565C8 " />
              </HoverCardTrigger>
              <HoverCardContent
                className="z-20 w-max rounded-lg bg-[#101828] px-3 pb-[6px] pt-1"
                isShownTriangle
                side="top"
                triangleClassName="bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 ml-0"
              >
                <span className="text-label-md font-[550] text-white">
                  {' '}
                  <AppFormattedMessage id="CALCULATED_BY" />
                </span>
              </HoverCardContent>
            </HoverCard>
          </div>
          <span className="text-xs font-[450] text-gray-700">
            <AppFormattedMessage id="ISSUED_SHARES" />:{' '}
            <AnimatedNumber
              integer
              value={
                stepOne?.eventDetails?.['share-issuance']?.event?.issuedSharesOnStart !== undefined
                  ? Number(stepOne?.eventDetails?.['share-issuance']?.event?.issuedSharesOnStart)
                  : Number(issuedShares)
              }
            />
          </span>
        </div>
        <div className="flex flex-col gap-3 rounded-md bg-white p-3 shadow-xs">
          <AnimatedCalculatedInput
            currencySign
            placeholder={<AppFormattedMessage id="VALUE" />}
            value={preMoneyValuation}
            wrapperClassName="w-full"
          />
          <DropDown
            answer={messagesLocale['PRE_MONEY_VALUATION_EXPLANATION']}
            className="rounded-lg bg-gray-50"
            question={<AppFormattedMessage id="VALUE_CALCULATION_INFO" />}
          />
        </div>
      </div>
      <DropDown
        answer={messagesLocale['SHARE_ISSUANCE_EVENT_EXPLANATION']}
        className="rounded-lg bg-gray-50"
        question={<AppFormattedMessage id="WHAT_IS_SHARE_ISSUANCE" />}
      />
    </div>
  );
};
